/* eslint-disable no-extend-native */
// Add here your array utils
import { sortBy as sortOn } from "lodash";

declare global {
    interface Array<T> {
        /**
         * Take the first number of items
         * @param num
         */
        first(num: number): Array<T>;
        /**
         * Take the first number of items (alias of first)
         * @param num
         */
        take(num: number): Array<T>;
        /**
         * Skip a number of items
         * @param num
         */
        skip(num: number): Array<T>;
        /**
         * Add a single item to the array
         * @param item
         */
        add(item: T): Array<T>;
        /**
         * Add a single item to the array if condition resolve as true
         * @param condition
         * @param item
         */
        pushIf(condition: any, item: T): number;
        /**
         * Add a single item to the array if not null or undefined
         * @param item
         */
        pushIfDefined(item: T): number;
        /**
         * Filter out unique items
         * @param key "name of the property to check uniqueness"
         */
        unique(key: string): Array<T>;
        /**
         * Immutable sort
         */
        sortBy(key: string, compareFunction?: (x: any, y: any) => number): Array<T>;
        /**
         * Filter
         * @param match "contains match"
         * @param fields "fields to search in"
         */
        filterBy(match: string, fields: string[]): Array<T>;
    }
}

if (!Array.prototype.first) {
    Array.prototype.first = function first<T>(this: T[], num: number): T[] {
        return this.slice(0, num);
    };
}

if (!Array.prototype.skip) {
    Array.prototype.skip = function skip<T>(this: T[], num: number): T[] {
        return this.slice(num, this.length);
    };
}

if (!Array.prototype.take) {
    Array.prototype.take = function take<T>(this: T[], num: number): T[] {
        // take is an alias of first
        return this.first(num);
    };
}

if (!Array.prototype.unique) {
    Array.prototype.unique = function unique<T>(this: T[], key: string): T[] {
        return [...new Map(this.map((item) => [item[key], item])).values()];
    };
}

if (!Array.prototype.add) {
    Array.prototype.add = function add<T>(this: T[], item: T): T[] {
        return [...this, item];
    };
}

if (!Array.prototype.pushIf) {
    Array.prototype.pushIf = function pushIf<T>(this: T[], condition, item: T): number {
        return condition ? this.push(item) : this.length;
    };
}

if (!Array.prototype.pushIfDefined) {
    Array.prototype.pushIfDefined = function pushIfDefined<T>(this: T[], item: T): number {
        return item ? this.push(item) : this.length;
    };
}

if (!Array.prototype.sortBy) {
    Array.prototype.sortBy = function sortBy<T>(this: T[], key: string, compareFunction?: (x, y) => number): T[] {
        if (!key) {
            return this;
        }
        let propertyKey = key;
        let reverse = false;
        let result = [];
        if (key.startsWith("-")) {
            reverse = true;
            propertyKey = key.substring(1);
        }
        if (compareFunction) {
            const getElement = (obj: T) =>
                propertyKey.split(".").reduce((acc, keyElement) => {
                    return acc[keyElement];
                }, obj);

            result = [...this].sort((x, y) => {
                const objX = getElement(x);
                const objY = getElement(y);

                return compareFunction(objX, objY);
            });
        } else {
            result = sortOn(this, propertyKey);
        }

        if (reverse) {
            return result.reverse();
        }
        return result;
    };
}

if (!Array.prototype.filterBy) {
    const innerSeparator = ".";

    const getValueAsString = (searchField: string, item, defaultValue = "") => {
        const splittedKey = searchField.split(innerSeparator);
        if (!item[splittedKey[0]]) {
            return defaultValue;
        }

        const firstKey = splittedKey.shift();

        if (Array.isArray(item[firstKey])) {
            return item[firstKey].reduce((acc, e) => {
                return getValueAsString(splittedKey.join("."), e, acc);
            }, defaultValue);
        }

        if (typeof item[firstKey] === "object") {
            return getValueAsString(splittedKey.join("."), item[firstKey], defaultValue);
        }

        return `${item[firstKey].toString()} ${defaultValue}`;
    };

    Array.prototype.filterBy = function unique<T>(this: T[], match: string, fields: string[]): T[] {
        const arrayFiltered = [];
        const valueToMatch = match.toLowerCase();
        for (let i = 0; i < this.length; i++) {
            const item = this[i];
            for (const searchField of fields) {
                const value = getValueAsString(searchField, item);
                if (JSON.stringify(value).toLowerCase().includes(valueToMatch)) {
                    arrayFiltered.push(item);
                    break;
                }
            }
        }
        return arrayFiltered;
    };
}
